import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const OPTIONS = [
  { key: "live", label: "Live", icon: "🔴", desc: "I'm hosting / on now", color: "from-red-600 to-rose-600", ring: "ring-red-500/40" },
  { key: "away", label: "Away", icon: "🟡", desc: "Around but not active", color: "from-amber-500 to-orange-500", ring: "ring-amber-500/40" },
  { key: "brb", label: "BRB", icon: "🟣", desc: "Back shortly", color: "from-purple-500 to-fuchsia-500", ring: "ring-purple-500/40" },
  { key: "offline", label: "Offline", icon: "⚫", desc: "Hide my status", color: "from-gray-700 to-gray-800", ring: "ring-gray-500/30" },
];

/**
 * Self-presence picker. Self-only — never let one user set another's status.
 * Mounted on the user's own dashboard / creator hub.
 */
const PresenceStatusPicker = ({ currentUser, onChange }) => {
  const [current, setCurrent] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser?.user_id) return;
    (async () => {
      try {
        const r = await api.get(`/users/${currentUser.user_id}/presence`);
        setCurrent(r.data?.presence_status || "offline");
        setNote(r.data?.presence_status_note || "");
      } catch {
        setCurrent("offline");
      }
    })();
  }, [currentUser?.user_id]);

  const handleSet = async (status) => {
    setSaving(true);
    try {
      const r = await api.put("/users/me/presence", { status, note: note || null });
      setCurrent(r.data?.presence_status || (status === "offline" ? "offline" : status));
      toast.success(status === "offline" ? "Status cleared" : `You're set to ${status.toUpperCase()}`);
      if (onChange) onChange(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  // iter_194 — Explicit note save + clear so users can edit their note
  // without having to re-tap their current status. Saves against whatever
  // status is currently active. Clearing sets note to null but keeps status.
  const persistNote = async (nextNote) => {
    if (!current || current === "offline") {
      // No active presence — silently keep the local note; nothing to persist.
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/users/me/presence", {
        status: current,
        note: nextNote || null,
      });
      setNote(r.data?.presence_status_note || "");
      toast.success(nextNote ? "Note saved" : "Note cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = () => persistNote(note.trim());
  const handleClearNote = () => { setNote(""); persistNote(""); };

  return (
    <Card className="p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/10 border-purple-500/30" data-testid="presence-status-picker">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-white font-bold text-base">⚡ Live presence</h3>
          <p className="text-gray-400 text-xs">Let listeners know when you're hosting or away.</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-gray-400">
          current: <span className="text-purple-200 font-semibold">{(current || "offline").toUpperCase()}</span>
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="presence-options">
        {OPTIONS.map((o) => {
          const isCurrent = current === o.key || (o.key === "offline" && (current === null || current === "offline"));
          return (
            <button
              key={o.key}
              onClick={() => handleSet(o.key)}
              disabled={saving}
              className={`text-left rounded-xl p-3 border transition bg-gradient-to-br ${o.color} ${isCurrent ? `ring-2 ${o.ring} border-white/30` : "border-white/10 opacity-80 hover:opacity-100"} disabled:opacity-50`}
              data-testid={`presence-option-${o.key}`}
            >
              <div className="text-2xl">{o.icon}</div>
              <div className="text-white font-bold text-sm mt-1">{o.label}</div>
              <div className="text-white/70 text-[11px]">{o.desc}</div>
            </button>
          );
        })}
      </div>
      <Input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 80))}
        placeholder="Optional note (e.g. 'back in 10 min')"
        className="bg-black/30 border-purple-500/30 text-white placeholder:text-gray-500"
        maxLength={80}
        data-testid="presence-note-input"
      />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSaveNote}
          disabled={saving || !current || current === "offline"}
          data-testid="presence-note-save-btn"
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold h-8"
        >
          Save note
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleClearNote}
          disabled={saving || !note}
          data-testid="presence-note-clear-btn"
          className="border-purple-500/40 text-purple-200 hover:bg-purple-500/20 h-8"
        >
          Clear note
        </Button>
        <span className="text-[10px] text-gray-500 ml-auto">
          {(current && current !== "offline") ? `Attached to ${current.toUpperCase()}` : "Set a status first to attach a note"}
        </span>
      </div>
      <p className="text-[10px] text-gray-500 mt-1">Status auto-clears after 30 min of inactivity.</p>
    </Card>
  );
};

export default PresenceStatusPicker;
